import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchBalanceReq,
  postLoginReq,
  fetchUserReq,
  updateSettingsReq,
  updateProfileReq,
  fetchActiveStoriesReq,
  fetchRefundBonusStatusReq,
  claimRefundBonusReq
} from './services';
import { setToken, backOfficeInstance, tmmInstance } from 'libraries/axios';
import { setCustomOddsMultiplier } from 'stores/betSlip';
import { deriveAllowedSportIds, setAllowedSportIds } from 'toolkit/allowedSports';

const initialState = {
  user: null,
  token: null,
  balance: 0,
  refundBonusBalance: 0,
  userSettings: null,
  userLimits: null,
  transactions: null,
  stories: [],
  refundBonusStatus: null,
  refundBonusLoading: false,
  refundBonusClaiming: false,
  // Claim cooldown: set only by claim responses
  refundBonusCooldownUntil: null,
  // Status back-off after a RATE_LIMITED status response; never affects the claim button
  refundBonusStatusRetryUntil: null
};

const resetRefundBonusState = (state) => {
  state.refundBonusStatus = null;
  state.refundBonusStatusRetryUntil = null;
  state.refundBonusCooldownUntil = null;
  state.refundBonusClaiming = false;
  state.refundBonusLoading = false;
};

export const login = createAsyncThunk('auth/login', async (body, { dispatch }) => {
  const response = await postLoginReq(body);
  dispatch(setCustomOddsMultiplier(response.customer.customerLimitDto.customOddsMultiplier));
  return response;
});

export const fetchUser = createAsyncThunk('auth/fetchUser', async (body, { dispatch }) => {
  const token = localStorage.getItem('token');
  if (token) {
    setToken(token);
    const response = await fetchUserReq(token);
    dispatch(
      setCustomOddsMultiplier(
        response.loggedCustomer.customer.customerLimitDto.customOddsMultiplier
      )
    );
    return response;
  }
});

export const fetchBalance = createAsyncThunk(
  'auth/fetchBalance',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchBalanceReq();
    } catch (error) {
      return rejectWithValue({ status: error.response?.status });
    }
  }
);

export const fetchRefundBonusStatus = createAsyncThunk('auth/fetchRefundBonusStatus', async () => {
  return await fetchRefundBonusStatusReq();
});

export const claimRefundBonus = createAsyncThunk('auth/claimRefundBonus', async () => {
  return await claimRefundBonusReq();
});

export const updateProfile = createAsyncThunk('auth/updateProfile', async (formData) => {
  await updateProfileReq(formData);
  return formData;
});

export const updateSettings = createAsyncThunk('auth/updateSettings', async (formData) => {
  await updateSettingsReq(formData);
  return formData;
});

export const fetchActiveStories = createAsyncThunk('auth/fetchActiveStories', async () => {
  const stories = await fetchActiveStoriesReq();
  return stories.map((group) => ({
    ...group,
    slides: group.slides.map((slide) => ({
      ...slide,
      matchId: slide.eventId || null
    }))
  }));
});

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      setAllowedSportIds(null);
      state.token = null;
      resetRefundBonusState(state);
      setToken(null);
      backOfficeInstance.defaults.headers.common.Authorization = null;
      tmmInstance.defaults.headers.common.Authorization = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state, action) => {
        const { customer, token } = action.payload;
        state.user = customer;
        state.token = token;
        state.userSettings = customer.customerSettingsDto;
        state.userLimits = customer.customerLimitDto;
        state.balance = customer.balance;
        resetRefundBonusState(state);
        setAllowedSportIds(deriveAllowedSportIds(customer));
        setToken(token);
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        if (action.payload) {
          const { loggedCustomer, token } = action.payload;
          state.user = loggedCustomer.customer;
          // CheckToken returns the token wrapped as { token: '...' }, unlike
          // CustomerSignIn which returns the plain string
          state.token = token?.token ?? null;
          state.userSettings = loggedCustomer.customer.customerSettingsDto;
          state.userLimits = loggedCustomer.customer.customerLimitDto;
          setAllowedSportIds(deriveAllowedSportIds(loggedCustomer.customer));
        }
      })
      .addCase(fetchUser.rejected, () => {
        setAllowedSportIds(null);
        setToken();
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.user = {
          ...state.user,
          ...action.payload
        };
      })
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.userSettings = {
          ...state.userSettings,
          ...action.payload
        };
      })
      .addCase(fetchBalance.fulfilled, (state, action) => {
        const { refundBonusBalance, balance } = action.payload;
        state.balance = balance;
        state.refundBonusBalance = refundBonusBalance;
      })
      .addCase(fetchBalance.rejected, (state, action) => {
        // Only an expired session invalidates the token; the 401 interceptor in
        // libraries/axios handles the redirect. Other failures (429, 5xx, network)
        // must not log the player out.
        if (action.payload?.status === 401) {
          setToken(null);
          backOfficeInstance.defaults.headers.common.Authorization = null;
          tmmInstance.defaults.headers.common.Authorization = null;
        }
      })
      .addCase(fetchRefundBonusStatus.pending, (state) => {
        state.refundBonusLoading = true;
      })
      .addCase(fetchRefundBonusStatus.fulfilled, (state, action) => {
        state.refundBonusLoading = false;
        const status = action.payload;
        if (status?.reason === 'RATE_LIMITED') {
          // Keep the last real status; only back off
          if (!state.refundBonusStatus) state.refundBonusStatus = status;
          state.refundBonusStatusRetryUntil = Date.now() + (status.retryAfterSeconds ?? 5) * 1000;
        } else {
          state.refundBonusStatus = status;
          state.refundBonusStatusRetryUntil = null;
        }
      })
      .addCase(fetchRefundBonusStatus.rejected, (state) => {
        state.refundBonusLoading = false;
      })
      .addCase(claimRefundBonus.pending, (state) => {
        state.refundBonusClaiming = true;
      })
      .addCase(claimRefundBonus.fulfilled, (state, action) => {
        state.refundBonusClaiming = false;
        const result = action.payload;
        if (result?.granted) {
          state.balance = result.balance;
          state.refundBonusBalance = result.refundBonusBalance;
        }
        if (result?.granted) {
          state.refundBonusCooldownUntil = Date.now() + (result.retryAfterSeconds ?? 30) * 1000;
        } else if (result?.reason === 'RATE_LIMITED' && result.retryAfterSeconds) {
          state.refundBonusCooldownUntil = Date.now() + result.retryAfterSeconds * 1000;
        }
        if (result && result.reason !== 'RATE_LIMITED') {
          // Never leave a stale eligible status behind if the follow-up refetch is rate limited
          const prev = state.refundBonusStatus ?? {};
          state.refundBonusStatus = {
            ...prev,
            eligible: false,
            reason: result.granted ? 'NO_NET_LOSS' : result.reason,
            estimatedAmount: 0,
            netLoss: 0,
            balance: result.balance,
            refundBonusBalance: result.refundBonusBalance,
            openBetCount: result.openBetCount ?? prev.openBetCount,
            minStake: result.minStake ?? prev.minStake,
            // A granted claim settles the deposit/withdraw rows, so both sums start again from 0
            depositAmount: result.granted ? 0 : (result.depositAmount ?? prev.depositAmount),
            withdrawAmount: result.granted ? 0 : (result.withdrawAmount ?? prev.withdrawAmount)
          };
        } else if (result?.reason === 'RATE_LIMITED' && !state.refundBonusStatus) {
          state.refundBonusStatus = result;
        }
      })
      .addCase(claimRefundBonus.rejected, (state) => {
        state.refundBonusClaiming = false;
      })
      .addCase(fetchActiveStories.fulfilled, (state, action) => {
        state.stories = action.payload;
      });
  }
});

export const { logout } = authSlice.actions;

export default authSlice.reducer;
